import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { useClassroom } from '../classroom/api';
import { rememberWeek } from '../planner/lastWeek';
import { useSubjects, useWeek, useWeekDetailsMutation, useWeekFieldMutation, useWeekLessonMutation } from '../planner/api';
import { PlannerSheet } from '../planner/sheet/PlannerSheet';
import { SheetTitle } from '../planner/sheet/SheetTitle';
import { currentWeek, mondayOf, neighbourDay, parseIsoWeek } from '../planner/weekMath';
import type { IsoWeek } from '../planner/weekMath';
import { logoUrl } from '../theme/applyTheme';
import { useTheme } from '../theme/api';

const DAY_MS = 86_400_000;

/** The day plan: one day of the week with, per lesson, a goal and materials (anything else, such as homework, is a field the teacher adds to a subject). */
export default function DayPage() {
  const params = useParams();
  const target = parseIsoWeek(params);
  const day = Number(params.dag);
  if (!target || !Number.isInteger(day) || day < 1 || day > 5) {
    const now = currentWeek();
    return <Navigate to={`/week/${now.year}/${now.week}`} replace />;
  }
  return <DayView key={`${target.year}-${target.week}-${day}`} target={target} day={day} />;
}

/** One day of the week, zoomed in: the same sheet with the day plan open under every lesson. */
function DayView({ target, day }: { target: IsoWeek; day: number }) {
  const week = useWeek(target.year, target.week);
  const subjects = useSubjects();
  const saveCell = useWeekLessonMutation(target.year, target.week);
  const saveDetails = useWeekDetailsMutation(target.year, target.week);
  const saveFields = useWeekFieldMutation(target.year, target.week);
  const theme = useTheme().data;
  const logo = theme ? logoUrl(theme) : null;
  const classroom = useClassroom().data;
  useEffect(() => rememberWeek({ year: target.year, week: target.week }), [target.year, target.week]);
  const date = new Date(mondayOf(target).getTime() + (day - 1) * DAY_MS);
  const title = new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(date);

  if (week.isPending || subjects.isPending) return <p className="panel m-4 p-6">Laden…</p>;
  if (week.isError || subjects.isError) {
    return (
      <p role="alert" className="panel m-4 p-6">
        De dag kon niet worden geladen. Vernieuw de pagina.
      </p>
    );
  }
  if (week.data === null) {
    return (
      <p className="panel m-4 p-6">
        Deze week bestaat nog niet.{' '}
        <Link to={`/week/${target.year}/${target.week}`} className="underline">
          Maak hem eerst aan
        </Link>
        .
      </p>
    );
  }

  return (
    <main className="mx-auto my-4 flex w-full max-w-5xl flex-col gap-3 px-3">
      <nav aria-label="Dag kiezen" className="no-print flex items-center gap-1">
        <Button asChild variant="ghost" className="min-h-11">
          <Link to={neighbourDay(target, day, -1)} aria-label="Vorige dag">
            ←
          </Link>
        </Button>
        <Button asChild variant="ghost" className="min-h-11">
          <Link to={neighbourDay(target, day, 1)} aria-label="Volgende dag">
            →
          </Link>
        </Button>
        <Button asChild variant="outline" className="min-h-11">
          <Link to={`/week/${target.year}/${target.week}`}>Hele week</Link>
        </Button>
      </nav>
      <PlannerSheet
        periods={week.data.periods.filter((period) => !period.isBreak)}
        subjects={subjects.data}
        lessons={week.data.lessons}
        day={day}
        week={target}
        title={<SheetTitle logoSrc={logo} name={classroom?.name ?? ''} />}
        onCellChange={(cell, value) => saveCell.mutateAsync({ ...cell, ...value }).catch(() => undefined)}
        onFieldsChange={(cell, values) => saveFields.mutate({ ...cell, values })}
        onDetailsChange={(cell, details) => saveDetails.mutate({ ...cell, ...details })}
      >
        <h1 className="text-2xl first-letter:uppercase">{title}</h1>
      </PlannerSheet>
    </main>
  );
}
